import {
  ApiScreeningOcrData,
  ApiScreeningRequest,
  ApiScreeningResponse,
} from "@/core/customers/entities/ApiDocumentUpload";
import {
  ScreeningResponse,
  ScreeningResult,
  OcrData,
} from "../entities/Screening";

/**
 * Data mapper for converting between domain models and API formats
 * Handles transformation of screening requests and responses
 */
export default class ScreeningDataMapper {
  static toApiRequest(): ApiScreeningRequest {
    // For now, return empty object since API doesn't require body
    // Parameter kept for future use if API requires body
    return {
      name: "",
      birthDate: "",
      country: "",
    };
  }

  /**
   * Maps API response to domain ScreeningResponse
   * @param res - API screening response (OCR in res.data; supports flat OCR at top level as fallback)
   * @returns Domain ScreeningResponse
   */
  static fromApiResponse(res: ApiScreeningResponse): ScreeningResponse {
    // Les deux branches portent une charge utile d'API, jamais une entité du domaine : l'ancienne
    // forme sert l'OCR à plat, la nouvelle sous `data`. Typer le repli sur la forme d'API — et non
    // sur `OcrData` — rend l'alias `nni` visible dans les deux cas ; sans lui, un NNI présent dans
    // une réponse à plat était perdu, et c'est un NNI vide qui partait à l'enregistrement.
    const ocrSource = res.data ?? (res as unknown as Partial<ApiScreeningOcrData>);
    const ocrData: OcrData | undefined = ocrSource
      ? {
          firstName: ocrSource.firstName,
          lastName: ocrSource.lastName,
          fullName:
            ocrSource.firstName && ocrSource.lastName
              ? `${ocrSource.firstName} ${ocrSource.lastName}`
              : undefined,
          dateOfBirth: ocrSource.dateOfBirth,
          placeOfBirth: ocrSource.placeOfBirth,
          dateOfIssue: ocrSource.dateOfIssue,
          expireDate: ocrSource.expireDate,
          placeOfIssue: ocrSource.placeOfIssue,
          countryOfIssue: ocrSource.countryOfIssue,
          age: ocrSource.age,
          documentNumber: ocrSource.documentNumber,
          // Le NNI arrive sous deux noms selon l'anciennete de la reponse : `nni` (nom metier)
          // ou `personalNumber` (nom Regula). Sans cette recopie le champ NNI du formulaire
          // reste vide, et c'est un NNI vide qui part a l'enregistrement du dossier.
          personalNumber: ocrSource.nni ?? ocrSource.personalNumber,
          job: ocrSource.job,
          sex: ocrSource.sex,
          nationality: ocrSource.nationality,
          portrait: ocrSource.portrait,
          documentValid: ocrSource.documentValid,
          documentExpired: ocrSource.documentExpired,
          errorMessage: ocrSource.errorMessage,
          address: ocrSource.address ?? undefined,
        }
      : undefined;

    // Handle screening status (for watchlist checks)
    // If we have screeningStatus, use it; otherwise default based on validity
    let screeningStatus: ScreeningResult["status"] = "clear"; // Default

    if (res.screeningStatus) {
      const normalizedStatus = res.screeningStatus.toLowerCase().trim();
      if (normalizedStatus === "review" || normalizedStatus === "not_clear") {
        screeningStatus = "review";
      } else if (normalizedStatus === "blocked") {
        screeningStatus = "blocked";
      }
      // "clear" is already the default, no need to set it again
    } else if (res.validity === false) {
      // If document validity is false, mark as review
      screeningStatus = "review";
    }

    return {
      success: true,
      message: res.message || "Screening completed successfully",
      result: {
        status: screeningStatus,
        provider: res.provider || "LSEG",
        matchScore: res.matchScore,
      },
      ocrData, // Include OCR data in response
      documentValidity: res.validity,
    };
  }
}
